<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { useToastStore } from '@/stores/toast'
import type { Toast, ToastType } from '@/types'

const toastStore = useToastStore()

const toastTypeConfig: Record<ToastType, { icon: string; bgClass: string; textClass: string; ariaLabel: string }> = {
  success: {
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    bgClass: 'bg-green-900 border-green-700',
    textClass: 'text-green-100',
    ariaLabel: 'Success'
  },
  error: {
    icon: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
    bgClass: 'bg-red-900 border-red-700',
    textClass: 'text-red-100',
    ariaLabel: 'Error'
  },
  info: {
    icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    bgClass: 'bg-blue-900 border-blue-700',
    textClass: 'text-blue-100',
    ariaLabel: 'Information'
  },
  warning: {
    icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    bgClass: 'bg-yellow-900 border-yellow-700',
    textClass: 'text-yellow-100',
    ariaLabel: 'Warning'
  }
}

function getConfig(type: ToastType) {
  return toastTypeConfig[type]
}

function handleDismiss(id: string) {
  toastStore.remove(id)
}

function handleKeyDown(event: KeyboardEvent, toast: Toast) {
  if (event.key === 'Escape' && toast.dismissible) {
    handleDismiss(toast.id)
  }
}

// Global keyboard handler for dismissing topmost toast
function handleGlobalKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape' && toastStore.toasts.length > 0) {
    const topToast = toastStore.toasts[toastStore.toasts.length - 1]
    if (topToast.dismissible) {
      handleDismiss(topToast.id)
    }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleGlobalKeyDown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeyDown)
})
</script>

<template>
  <Teleport to="body">
    <!-- aria-live region for screen reader announcements -->
    <div
      aria-live="polite"
      aria-atomic="true"
      class="sr-only"
    >
      <div v-for="toast in toastStore.toasts" :key="toast.id">
        {{ getConfig(toast.type).ariaLabel }}: {{ toast.message }}
      </div>
    </div>

    <!-- Visual toast container -->
    <div
      class="fixed z-50 flex flex-col gap-3 pointer-events-none
             bottom-4 right-4 max-w-sm w-full
             max-[400px]:bottom-auto max-[400px]:top-4 max-[400px]:right-2 max-[400px]:left-2 max-[400px]:max-w-none max-[400px]:w-auto"
      role="region"
      aria-label="Notifications"
    >
      <TransitionGroup
        name="toast"
        tag="div"
        class="flex flex-col gap-3"
      >
        <div
          v-for="toast in toastStore.toasts"
          :key="toast.id"
          :class="[
            'pointer-events-auto rounded-lg border p-4 shadow-lg flex items-start gap-3 transition-all',
            getConfig(toast.type).bgClass,
            getConfig(toast.type).textClass
          ]"
          role="alert"
          :aria-labelledby="`toast-title-${toast.id}`"
          tabindex="0"
          @keydown="handleKeyDown($event, toast)"
        >
          <!-- Icon -->
          <svg
            class="w-5 h-5 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              :d="getConfig(toast.type).icon"
            />
          </svg>

          <!-- Message -->
          <p :id="`toast-title-${toast.id}`" class="flex-1 text-sm font-medium">
            {{ toast.message }}
          </p>

          <!-- Dismiss button -->
          <button
            v-if="toast.dismissible"
            type="button"
            class="flex-shrink-0 p-1 -m-1 rounded hover:bg-white/10 transition-colors focus-ring min-w-[44px] min-h-[44px] flex items-center justify-center"
            :aria-label="`Dismiss ${getConfig(toast.type).ariaLabel.toLowerCase()} notification`"
            @click="handleDismiss(toast.id)"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-move {
  transition: transform 0.3s ease;
}

/* On very small screens (< 400px), animate from top instead of right */
@media (max-width: 400px) {
  .toast-enter-from {
    opacity: 0;
    transform: translateY(-100%);
  }

  .toast-leave-to {
    opacity: 0;
    transform: translateY(-100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .toast-enter-active,
  .toast-leave-active,
  .toast-move {
    transition: none;
  }

  .toast-enter-from,
  .toast-leave-to {
    opacity: 0;
    transform: none;
  }
}
</style>
